import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Row, Col, Select, Card } from "antd";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend
} from "recharts";
import dayjs from "dayjs";

const { Option } = Select;

// Time filters
const timeRanges = [
  { label: "Today", value: "today" },
  { label: "Last 7 Days", value: "last7" },
  { label: "Last 30 Days", value: "last30" },
  { label: "This Year", value: "thisYear" },
  { label: "Last Year", value: "lastYear" }
];

const LogAnalytics = () => {
  const analyticsData = useSelector((state) => state.analytics.visits);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedRange, setSelectedRange] = useState("today");

  useEffect(() => {
    filterData(selectedRange);
  }, [analyticsData, selectedRange]);

  // Convert Firestore timestamps to JS Date
  const convertTimestamp = (timestamp) => {
    return new Date(timestamp.seconds * 1000 + timestamp.nanoseconds / 1e6);
  };

  const filterData = (range) => {
    const now = dayjs();
    let startDate, endDate;

    switch (range) {
      case "today":
        startDate = now.startOf("day");
        endDate = now.endOf("day");
        break;
      case "last7":
        startDate = now.subtract(7, "days").startOf("day");
        endDate = now.endOf("day");
        break;
      case "last30":
        startDate = now.subtract(30, "days").startOf("day");
        endDate = now.endOf("day");
        break;
      case "thisYear":
        startDate = now.startOf("year");  // e.g., 2025-01-01T00:00:00
        endDate = now.endOf("year");  // e.g., 2025-12-31T23:59:59
        break;
      case "lastYear":
        startDate = now.subtract(1, "year").startOf("year"); // 2024-01-01T00:00:00
        endDate = now.subtract(1, "year").endOf("year"); // 2024-12-31T23:59:59
        break;
      default:
        return;
    }

    const filtered = analyticsData
      ?.sort((a, b) => b.time - a.time)
      .filter((log) => {
        const logTime = dayjs(convertTimestamp(log.timestamp));
        return logTime.isAfter(startDate) && logTime.isBefore(endDate);
      })
      .map((log) => ({
        ...log,
        formattedDate: dayjs(convertTimestamp(log.timestamp)).format("YYYY-MM-DD"),
      }));

    setFilteredData(filtered);
  };


  // Group data by date for line charts
  const groupByDate = (key) => {
    const counts = filteredData.reduce((acc, log) => {
      const date = log.formattedDate;
      if (!acc[date]) acc[date] = { date };
      acc[date][log[key]] = (acc[date][log[key]] || 0) + 1;
      return acc;
    }, {});
  
    return Object.values(counts).sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  };

  // Count categories for bar charts
  const countByCategory = (key) => {
    return filteredData.reduce((acc, log) => {
      acc[log[key]] = (acc[log[key]] || 0) + 1;
      return acc;
    }, {});
  };

  // Prepare data for charts
  const pageData = groupByDate("page");
  const locationData = groupByDate("location");
  const osData = Object.entries(countByCategory("os")).map(([name, count]) => ({ name, count }));
  const deviceData = Object.entries(countByCategory("device")).map(([name, count]) => ({ name, count }));
  const browserData = Object.entries(countByCategory("browser")).map(([name, count]) => ({ name, count }));

  return (
    < >
      <Col span={24} style={{
        background: 'white',
        border: '1px solid var(--primary)',
        justifyContent: 'space-evenly',
        display: 'flex',
        alignItems: 'center',
        marginBottom: '20px'
        // gap:'10px'
      }}>
        <Select value={selectedRange} onChange={setSelectedRange} style={{ width: 200, margin: 6 }}>
          {timeRanges.map((range) => (
            <Option key={range.value} value={range.value}>
              {range.label}
            </Option>
          ))}
        </Select>
      </Col>


      <Row gutter={[16, 16]}>

        {/* Line Chart - Page Visits */}
        <Col xs={24} md={12}>
          <Card className="logs-card" title="Page Visits Over Time">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={pageData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                {Object.keys(pageData[0] || {}).filter((k) => k !== "date").map((key, i) => (
                  <Line key={key} type="monotone" dataKey={key} stroke={`#${Math.floor(Math.random() * 16777215).toString(16)}`} strokeWidth={2} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Line Chart - Location Trends */}
        <Col xs={24} md={12}>
          <Card className="logs-card" title="Location Trends Over Time">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={locationData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                {Object.keys(locationData[0] || {}).filter((k) => k !== "date").map((key, i) => (
                  <Line key={key} type="monotone" dataKey={key} stroke={`#${Math.floor(Math.random() * 16777215).toString(16)}`} strokeWidth={2} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Bar Chart - OS Distribution */}
        <Col xs={24} md={12}>
          <Card className="logs-card" title="OS Distribution">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={osData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#34A853" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Bar Chart - Device Usage */}
        <Col xs={24} md={12}>
          <Card className="logs-card" title="Device Usage">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={deviceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#FBBC05" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Bar Chart - Browser Usage */}
        <Col xs={24}>
          <Card className="logs-card" title="Browser Usage">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={browserData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#3543BA" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default LogAnalytics;
